import React, { useState, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const NotificationLayer = () => {
    const { auth } = useAuth();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [targetGroup, setTargetGroup] = useState('everyone');
    const [targetValue, setTargetValue] = useState('');
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchPositions = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/positions`);
            if (response.ok) {
                const data = await response.json();
                setPositions(data);
            }
        } catch (error) {
            console.error('Failed to fetch positions', error);
        }
    }, [auth]);

    useEffect(() => {
        // Fetch positions if "position" group is selected
        if (targetGroup === 'position' && positions.length === 0) {
            fetchPositions();
        }
        // Reset targetValue when group changes
        setTargetValue('');
    }, [targetGroup, positions.length, fetchPositions]);

    const handleSend = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/notifications/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    title,
                    body,
                    targetGroup,
                    targetValue: (targetGroup === 'position' || targetGroup === 'individual') ? targetValue : null
                })
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(`Sent successfully to ${data.recipientCount} recipients!`);
                setTitle('');
                setBody('');
                setTargetValue('');
            } else {
                toast.error(data.error || 'Failed to send notification');
            }
        } catch (error) {
            console.error('Network error:', error);
            toast.error('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden">
            <div className="card-body p-40">
                <h5 className="mb-20">Send Push Notification</h5>
                <form onSubmit={handleSend}>
                    <div className="row">
                        {/* Title */}
                        <div className="col-sm-12">
                            <div className="mb-20">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    Notification Title
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    placeholder="Enter title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Body */}
                        <div className="col-sm-12">
                            <div className="mb-20">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    Message Content
                                </label>
                                <textarea
                                    className="form-control radius-8"
                                    placeholder="Enter message content"
                                    rows="3"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Target Group */}
                        <div className="col-sm-6">
                            <div className="mb-20">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    Target Audience
                                </label>
                                <select
                                    className="form-control radius-8"
                                    value={targetGroup}
                                    onChange={(e) => setTargetGroup(e.target.value)}
                                >
                                    <option value="everyone">Everyone</option>
                                    <option value="employees">All Employees</option>
                                    <option value="position">Specific Position</option>
                                    <option value="business_owners">All Business Owners</option>
                                    <option value="customers">All Customers</option>
                                    <option value="individual">Individual (Email)</option>
                                </select>
                            </div>
                        </div>

                        {/* Conditional Inputs */}
                        {targetGroup === 'position' && (
                            <div className="col-sm-6">
                                <div className="mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Select Position
                                    </label>
                                    <select
                                        className="form-control radius-8"
                                        value={targetValue}
                                        onChange={(e) => setTargetValue(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Select Position --</option>
                                        {positions.map(pos => (
                                            <option key={pos.id} value={pos.id}>{pos.title}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {targetGroup === 'individual' && (
                            <div className="col-sm-6">
                                <div className="mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        User Email
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control radius-8"
                                        placeholder="user@example.com"
                                        value={targetValue}
                                        onChange={(e) => setTargetValue(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="col-sm-12 mt-3">
                            <button 
                                type="submit" 
                                className="btn btn-primary-600 px-24 py-12 radius-8 w-100"
                                disabled={loading}
                            >
                                {loading ? 'Sending...' : 'Send Notification'}
                            </button>
                        </div>
                    </div>
                </form>
                <ToastContainer />
            </div>
        </div>
    );
};

export default NotificationLayer;